import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { hubTable, hubTableIntro, formatApiErrorDetail } from "@/lib/api";
import { Check } from "lucide-react";

export default function TheTable() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["hub-table"],
    queryFn: hubTable,
    retry: false,
  });
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("idle");
  const [err, setErr] = useState("");

  const isForbidden = error?.response?.status === 403;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!note.trim()) { setErr("Tell us who you would like to meet."); return; }
    setStatus("submitting");
    try {
      await hubTableIntro({ note });
      setStatus("success");
    } catch (e2) {
      setStatus("idle");
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || "Something went wrong.");
    }
  };

  if (isLoading) return <p className="cv-body text-[#9A938A]">Loading...</p>;

  if (isForbidden) {
    return (
      <div data-testid="hub-table-upsell">
        <p className="cv-kicker">The Table</p>
        <h1 className="cv-display text-3xl mt-4">Members only.</h1>
        <p className="cv-body text-[#1F1B16]/75 mt-4 max-w-xl">
          The Table is the membership room. A standing calendar and a direct line to introductions worth making.
        </p>
        <Link to="/the-table" className="cv-btn-primary mt-7 inline-flex" data-testid="hub-table-join-link">Join The Table</Link>
      </div>
    );
  }

  return (
    <div data-testid="hub-table">
      <p className="cv-kicker">The Table</p>
      <h1 className="cv-display text-3xl mt-4">The room.</h1>

      <div className="mt-8">
        <p className="cv-kicker">Room calendar</p>
        <div className="mt-4 divide-y divide-[rgba(28,27,25,0.14)] border-t border-[rgba(28,27,25,0.14)]">
          {(data?.rooms || []).map((r) => (
            <div key={r.slug || r.title} className="py-4 flex items-center justify-between">
              <span className="cv-body text-sm">{r.title}</span>
              <span className="cv-serif text-sm text-[#1F1B16]/70">{r.event_date}</span>
            </div>
          ))}
          {(!data?.rooms || data.rooms.length === 0) && (
            <p className="cv-body text-sm text-[#9A938A] py-4">The next room dates are being scheduled.</p>
          )}
        </div>
      </div>

      <div className="mt-10 max-w-md">
        <p className="cv-kicker">Request an introduction</p>
        {status === "success" ? (
          <div data-testid="table-intro-success" className="mt-4 flex items-start gap-3">
            <span className="w-8 h-8 rounded-full border border-[#A6633C] flex items-center justify-center shrink-0"><Check size={16} className="text-[#A6633C]" /></span>
            <p className="cv-body text-sm text-[#1F1B16]/80">Intro requested. Rakia will make the connection.</p>
          </div>
        ) : (
          <form data-testid="table-intro-form" onSubmit={submit} className="mt-4 space-y-3">
            <textarea data-testid="table-intro-note" rows={3} className="cv-input resize-none" placeholder="Who would you like to meet, and why?" value={note} onChange={(e) => setNote(e.target.value)} />
            {err && <p data-testid="table-intro-error" className="cv-body text-sm text-[#1F1B16] border-l-2 border-[#A65A43] pl-3">{err}</p>}
            <button data-testid="table-intro-submit" type="submit" disabled={status === "submitting"} className="cv-btn-primary disabled:opacity-60">Request intro</button>
          </form>
        )}
      </div>
    </div>
  );
}
